import React from 'react';
import { WrappedComponentProps } from 'react-intl';

import { Field, Form, FormProps } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';

import Label from '../../../../components/common/label/label';
import { Translate } from '../../../../components/translate/translate';
import { FormSubmittingOverlay } from '../../../flows/flow-details-panel/common/form-components/form-submitting-overlay/form-submitting-overlay';
import { TeamMember, TeamMemberFormValues } from '../../team.types';

import { TeamMemberEmailsMultiselect } from './team-member-emails-multiselect/team-member-emails-multiselect';
import { TeamMemberFormFields } from './team-member-form-fields/team-member-form-fields';
import { TeamMemberFormFooter } from './team-member-form-footer/team-member-form-footer';
import styles from './team-member-form.scss';
import { validateTeamMemberForm } from './team-member-form.validation';
import { TeamMemberFormAccessPermissions } from '../../team.constants';

export const TEAM_MEMBER_ADD_FORM_NAME = 'teamMemberAddForm';

export interface TeamMemberFormOwnProps {
  onAddTeamMemberAccess(): void;
  onCancel(): void;
}

export interface TeamMemberFormStateProps {
  members: TeamMember[];
  initialValues: any;
}

export interface TeamMemberFormDispatchProps {
  onSubmit(data: TeamMemberFormValues): Promise<void>;
}

export type TeamMemberFormProps = TeamMemberFormOwnProps &
  TeamMemberFormStateProps &
  TeamMemberFormDispatchProps &
  WrappedComponentProps &
  FormProps<TeamMemberFormValues, TeamMemberFormOwnProps>;

export const TeamMemberForm: React.FunctionComponent<TeamMemberFormProps> = ({
  intl,
  onAddTeamMemberAccess,
  initialValues,
  onCancel,
  onSubmit,
  members,
}: TeamMemberFormProps) => {
  return (
    <Form
      onSubmit={onSubmit}
      mutators={{ ...arrayMutators }}
      initialValues={initialValues}
      validate={(value) => {
        return validateTeamMemberForm(intl, members);
      }}
    >
      {({
        handleSubmit,
        submitting,
        invalid,
        form: {
          mutators: { pop, push },
        },
      }) => (
        <form
          className={styles.form}
          data-hook="team-member-form"
          onSubmit={handleSubmit}
        >
          <FormSubmittingOverlay />

          <div>
            <Label htmlFor="emails">
              <Translate transKey="team.add-member-modal.form.emails.title" />
            </Label>

            <Field
              id="emails"
              name="emails"
              dataHook="team-member-form-emails-multiselect"
              component={TeamMemberEmailsMultiselect}
              props={{ dataHook: 'team-member-form-emails-multiselect' } as any}
              autoFocus
              placeholder={intl.formatMessage({
                id: 'team.add-member-modal.form.emails.placeholder',
              })}
            />
          </div>

          <hr className={styles.separator} />

          <div>
            <Label htmlFor="accessLevel">
              <Translate transKey="team.add-member-modal.form.access-level.title" />
            </Label>

            <FieldArray
              name="securityGroups"
              component={TeamMemberFormFields}
              rerenderOnEveryChange={true}
            />
          </div>

          <TeamMemberFormFooter
            submitting={submitting}
            className={styles.footer}
            onAddTeamMemberAccess={() => {
              push('securityGroups', {
                level: undefined,
                permissions: TeamMemberFormAccessPermissions.Viewer,
                projectsIds: [],
              });
            }}
            onCancel={onCancel}
            invalid={invalid}
            submitButtonText={
              <Translate transKey="team.add-member-modal.confirm-text" />
            }
          />
        </form>
      )}
    </Form>
  );
};
